import {FC, useState} from "react";
import {AdminTable, AdminTableProps} from "@/components/common/table";
import {GRID_CHECKBOX_SELECTION_COL_DEF, GridSelectionModel} from "@mui/x-data-grid-pro";
import {IActiveUserDto} from "@/types/admin";
import {useTeamProgressContext} from "@/contexts/TeamProgressContext";
import {IBaseSearchDTO} from "@/components/common/table/AdminTable";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import dayjs from "dayjs";
import {TeamProgressSection, TeamProgressWidget} from "@/enums";
import {useManageTeamProgressReport} from "../../hooks/useDownloadReport";
import {useExportTeamProgressContext} from "@/contexts/ExportTeamProgressContext";
import {formatDateCell} from "@/utils/date-formatters";
import { useNavigate } from "react-router-dom";
import { getDateTo } from "../../utils/common-utils";

interface IActiveUsersSearchDto extends IBaseSearchDTO {
	archivedUsers?: boolean;
	dateFrom?: string;
	dateTo?: string;
}

const ActiveUsersDataGrid: FC = () => {
	const [selected, setSelected] = useState<GridSelectionModel>([]);
	const {filters, updateSectionState} = useTeamProgressContext();
	const {exportTaskExists} = useExportTeamProgressContext();
	const {initDownload, handleUpdatedFilter, handleUpdatedSort} = useManageTeamProgressReport(TeamProgressWidget.activeUsers)
	const {t} = useSSTranslation();
	const isExporting = exportTaskExists(TeamProgressWidget.activeUsers);
	const navigate = useNavigate();

	const getHeadCells = (): AdminTableProps<"user", IActiveUserDto, IActiveUsersSearchDto>["columns"] => ([
		{
			...GRID_CHECKBOX_SELECTION_COL_DEF,
			type: "string",
			hideable: false
		},
		{
			field: "firstname",
			flex: 1,
			headerName: t("completed_lessons_column_firstname", "First Name"),
			type: "string"
		},
		{
			field: "lastname",
			flex: 1,
			headerName: t("completed_lessons_column_lastname", "Last Name"),
			type: "string"
		},
		{
			field: "timeInTraining",
			flex: 1,
			headerName: t("time_in_training", "Total Watch Time"),
			type: "string",
			filterable: false
		},
		{
			field: "completedLessons",
			flex: 1,
			headerName: t("completed_lessons", "Completed Lessons"),
			type: "number"
		},
		{
			field: "supervisor",
			flex: 1,
			type: "string",
			headerName: t("completed_lessons_column_supervisor", "Supervisor")
		},
		{
			field: "division",
			flex: 1,
			type: "string",
			headerName: t("completed_lessons_column_division", "Division")
		},
		{
			field: "streak",
			flex: 1,
			headerName: t("completed_lessons_column_streak", "Streak"),
			type: "number"
		},
		{
			field: "lastActive",
			flex: 1,
			type: "date",
			headerName: "Last Active",
			valueGetter: (e) => formatDateCell(e.value)
		},
		{
			field: "email",
			flex: 1,
			headerName: t("completed_lessons_column_email", "Email"),
			type: "string"
		},
		{
			field: "phone",
			flex: 1,
			headerName: t("completed_lessons_column_phone", "Phone"),
			type: "string"
		},
		{
			field: "status",
			flex: 1,
			headerName: t("completed_lessons_column_status", "Status"),
			type: "string"
		},
		{
			field: "jobCategory",
			flex: 1,
			headerName: t("completed_lessons_column_job_category", "Job Category"),
			type: "string"
		},
		{
			field: "jobTitle",
			flex: 1,
			headerName: t("completed_lessons_column_job_title", "Job Title"),
			type: "string"
		}
	]);

	const headCells = getHeadCells();

	// Create translated filter description
	const getFilterDescription = (): string => {
		const {dateRangeType, dateFrom, dateTo} = filters;

		switch (dateRangeType) {
			case "lastWeek":
				return t("completed_lessons_viewing_stats_past_week", "VIEWING STATS FROM THE PAST WEEK");
			case "lastMonth":
				return t("completed_lessons_viewing_stats_past_month", "VIEWING STATS FROM THE PAST MONTH");
			case "lastYear":
				return t("completed_lessons_viewing_stats_past_year", "VIEWING STATS FROM THE PAST YEAR");
			case "customDateRange": {
				const fromDate = dayjs(dateFrom).format("MM/DD/YYYY");
				const toDate = dayjs(dateTo).format("MM/DD/YYYY");
				return `${t("completed_lessons_viewing_stats_custom_range", "")} ${fromDate} - ${toDate}`;
			}
			default:
				return t("completed_lessons_viewing_stats_all_time", "VIEWING STATS FROM ALL TIME");
		}
	};

	const handleUpdateDatagridState = (isLoading: boolean) => {
		updateSectionState(isLoading, TeamProgressSection.dataGrid);
	}

	const filterDescription = getFilterDescription();

	const searchDTOverride: Partial<IActiveUsersSearchDto> = {
		archivedUsers: filters.archivedUsers,
		dateFrom: dayjs(filters.dateFrom).format("YYYY-MM-DD"),
		dateTo: getDateTo(filters.dateTo)
	};

	return (
		<AdminTable<"user", IActiveUserDto, IActiveUsersSearchDto>
			enableAdvancedStringFilters={true}
			url="/api/v1/team-progress/active-users"
			type="user"
			onEditAction={e => navigate("/admin/users/" + e.id.toString())}
			columns={headCells}
			disableColumnResize={false}
			selectionModel={selected}
			onSelectionModelChange={e => setSelected(e)}
			searchDTOverride={() => searchDTOverride}
			checkboxSelection={false}
			initialState={{
				columns: {
					columnVisibilityModel: {
						jobCategory: false,
						jobTitle: false,
						email: false,
						phone: false,
						status: false
					}
				}
			}}
			toolBarText={filterDescription}
			exportToolTipText={t("data_grid_export_tooltip", "Coming Soon")}
			disableExport
			showAlternativeExportoption
			isExporting={isExporting}
			onClickAlternativeExportOption={initDownload}
			updatedFilterCallback={handleUpdatedFilter}
			updatedSortCallback={handleUpdatedSort}				
			isLoadingCallback={handleUpdateDatagridState}
		/>
	);
}
export default ActiveUsersDataGrid;
